export const FEE_LEVEL_CONTRACT_ITEM = 'contract_item';
export const FEE_LEVEL_FEE_COST_CODE = 'fee_cost_code';
export const FEE_LEVEL_LINE_MARKUP_FEE_LINE = 'line_markup_fee_line';

export const FEE_LEVEL_OPTIONS = [
  {
    value: FEE_LEVEL_CONTRACT_ITEM,
    label: 'Inline on Contract Items',
    description: 'Each contract item maintains its own fee markup.',
  },
  {
    value: FEE_LEVEL_FEE_COST_CODE,
    label: 'Designated Fee Line',
    description: 'A project fee percentage is applied to applicable costs and billed to a designated fee line.',
  },
  {
    value: FEE_LEVEL_LINE_MARKUP_FEE_LINE,
    label: 'Inline, Billed on a Single Fee Line',
    description: 'Fee markups are maintained by contract item, but calculated fees are aggregated and billed on a single designated fee line.',
  },
];

export function usesFeeCostCodeLine(feeLevel) {
  return feeLevel === FEE_LEVEL_FEE_COST_CODE || feeLevel === FEE_LEVEL_LINE_MARKUP_FEE_LINE;
}

export function usesLineMarkups(feeLevel) {
  return feeLevel === FEE_LEVEL_CONTRACT_ITEM || feeLevel === FEE_LEVEL_LINE_MARKUP_FEE_LINE;
}

export function feeLevelLabel(feeLevel) {
  const option = FEE_LEVEL_OPTIONS.find((row) => row.value === feeLevel);
  return option ? option.label : FEE_LEVEL_OPTIONS[0].label;
}

export function lineFeeBase(billingAmount, scheduledLaborAmount, applyFeeToLaborSchedule) {
  const billing = Number(billingAmount || 0);
  const scheduled = Number(scheduledLaborAmount || 0);
  if (applyFeeToLaborSchedule) {
    return Math.round(billing * 100) / 100;
  }
  return Math.max(0, Math.round((billing - scheduled) * 100) / 100);
}

export function lineFeeAmount(billingAmount, scheduledLaborAmount, feePercent, applyFeeToLaborSchedule) {
  const base = lineFeeBase(billingAmount, scheduledLaborAmount, applyFeeToLaborSchedule);
  const percent = Number(feePercent || 0);
  return Math.round((base * percent / 100) * 100) / 100;
}

export function billedAmountWithLineMarkup(
  billingAmount,
  scheduledLaborAmount,
  markupPercent,
  applyFeeToLaborSchedule
) {
  const billing = Number(billingAmount || 0);
  const scheduled = Number(scheduledLaborAmount || 0);
  const markup = Number(markupPercent || 0);
  if (applyFeeToLaborSchedule) {
    return Math.round((billing * (1 + markup / 100)) * 100) / 100;
  }
  const feeBase = Math.max(0, billing - scheduled);
  return Math.round((feeBase * (1 + markup / 100) + scheduled) * 100) / 100;
}

export function isDesignatedFeeLine(line, feeLevel, feeCostCodeVuid, feeCostTypeVuid) {
  if (!line) return false;
  if (line.is_fee_line) return true;
  if (!usesFeeCostCodeLine(feeLevel) || !feeCostCodeVuid || !feeCostTypeVuid) {
    return false;
  }
  return line.cost_code_vuid === feeCostCodeVuid && line.cost_type_vuid === feeCostTypeVuid;
}

export function calculatedFeesFromLines(lines, feeData) {
  const feeLevel = feeData?.fee_level;
  if (usesFeeCostCodeLine(feeLevel)) {
    return Math.round((lines || []).reduce((sum, line) => {
      if (!isDesignatedFeeLine(
        line,
        feeLevel,
        feeData?.fee_cost_code_vuid,
        feeData?.fee_cost_type_vuid
      )) {
        return sum;
      }
      return sum + (Number(line.actual_billing_amount) || 0);
    }, 0) * 100) / 100;
  }
  return Math.round((lines || []).reduce((sum, line) => {
    return sum + lineFeeAmount(
      line.billing_amount,
      line.scheduled_labor_amount,
      line.markup_percentage,
      line.apply_fee_to_labor_schedule
    );
  }, 0) * 100) / 100;
}
